import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { COLORS } from './config';
import { s, buildScoreMap, getAvgScore } from './components';
import {
  LoginScreen, HomeScreen, CollectionScreen, DetailScreen,
  TastingScreen, AddBottleScreen, UpdateFillScreen, WishlistScreen,
  TastingSearchScreen,
} from './screens';

if (typeof document !== 'undefined') {
  document.documentElement.style.backgroundColor = COLORS.bg;
  document.body.style.backgroundColor = COLORS.bg;
  document.body.style.margin = '0';
  document.body.style.padding = '0';
}

export default function App() {
  const [screen, setScreen] = useState('home');
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [addBottleInitialName, setAddBottleInitialName] = useState('');
  const [tastingKey, setTastingKey] = useState(0);
  const [collectionState, setCollectionState] = useState({
    filter: 'All', typeFilter: 'All', sort: 'name', sortDir: 'asc', mode: 'collection',
  });
  const [bottles, setBottles] = useState([]);
  const [scoreMap, setScoreMap] = useState({});
  const [recentTastings, setRecentTastings] = useState([]);
  const [totalNotes, setTotalNotes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setCheckingAuth(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadData();
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bottlesData, notesData, recentData] = await Promise.all([
        supabase.from('bottles').select('*'),
        supabase.from('tasting_notes').select('id,bottle_id,overall_score,tasting_date,would_reach_again'),
        supabase.from('tasting_notes')
          .select('id,bottle_id,overall_score,tasting_date,serving_style,overall_notes,nose_notes,palate_notes,finish_notes,is_experience,would_reach_again')
          .order('tasting_date', { ascending: false })
          .limit(10),
      ]);
      if (Array.isArray(bottlesData.data)) setBottles(bottlesData.data);
      if (Array.isArray(notesData.data)) {
        setScoreMap(buildScoreMap(notesData.data));
        setTotalNotes(notesData.data.length);
      }
      if (Array.isArray(recentData.data)) setRecentTastings(recentData.data);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const handleUpdateBottle = (updatedBottle) => {
    setBottles(prev => prev.map(b => b.id === updatedBottle.id ? updatedBottle : b));
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setBottles([]); setScoreMap({}); setRecentTastings([]); setTotalNotes(0);
    setScreen('home');
  };

  const exportData = async () => {
    try {
      const [bottlesRes, notesRes, wishlistRes, flavorsRes] = await Promise.all([
        supabase.from('bottles').select('*'),
        supabase.from('tasting_notes').select('*'),
        supabase.from('wishlist').select('*'),
        supabase.from('flavor_selections').select('*'),
      ]);

      const exportObj = {
        exported_at: new Date().toISOString(),
        user_id: user?.id,
        bottles: bottlesRes.data || [],
        tasting_notes: notesRes.data || [],
        flavor_selections: flavorsRes.data || [],
        wishlist: wishlistRes.data || [],
      };

      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `barrelnotes-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  const navigate = (screenName, payload) => {
    if (screenName === 'edit-tasting') {
      setSelectedNote(payload.note);
      setSelectedBottle(payload.bottle);
    } else if (screenName === 'add-bottle') {
      setAddBottleInitialName(payload?.initialName || '');
      setSelectedBottle(null);
    } else if (screenName === 'collection') {
      if (payload?.initialFilter) setCollectionState(prev => ({ ...prev, filter: payload.initialFilter }));
      if (payload?.initialMode) setCollectionState(prev => ({ ...prev, mode: payload.initialMode }));
      setSelectedBottle(null);
    } else if (screenName === 'detail') {
      setSelectedBottle(payload);
      setSelectedNote(null);
    } else {
      setSelectedBottle(payload);
      setSelectedNote(null);
    }
    setScreen(screenName);
    window.scrollTo(0, 0);
  };

  const navItems = [
    { id: 'home', icon: '🏠', label: 'Home' },
    { id: 'collection', icon: '🥃', label: 'Collection' },
    { id: 'tasting', icon: '📝', label: 'Tasting' },
    { id: 'search', icon: '📓', label: 'Journal' },
    { id: 'wishlist', icon: '🎯', label: 'Wishlist' },
  ];

  if (checkingAuth) {
    return (
      <div style={{ backgroundColor: COLORS.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, fontFamily: 'Georgia, serif' }}>
        <div style={{ fontSize: 48 }}>🥃</div>
        <p style={{ color: COLORS.textMuted, letterSpacing: 2, fontSize: 13 }}>Loading...</p>
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  if (loading) {
    return (
      <div style={{ backgroundColor: COLORS.bg, minHeight: '100vh', maxWidth: 430, margin: '0 auto', fontFamily: 'Georgia, serif' }}>
        <div style={s.header}>
          <div><h1 style={s.logo}>BarrelNotes</h1><p style={s.logoSub}>YOUR BOURBON JOURNAL</p></div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[1,2,3].map(i => <div key={i} style={{ flex: 1, height: 70, background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`, border: `1px solid ${COLORS.border}`, borderRadius: 10 }} />)}
          </div>
          {[1,2,3,4,5].map(i => <div key={i} style={{ height: 90, background: `linear-gradient(135deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`, border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 10 }} />)}
        </div>
      </div>
    );
  }

  const activeTastingScreen = screen === 'tasting' || screen === 'edit-tasting';
  const activeNav = activeTastingScreen ? 'tasting' : screen;

  return (
    <div style={{ ...s.app, overflow: 'hidden', height: '100vh' }}>
      <div style={s.header}>
        <div><h1 style={s.logo}>BarrelNotes</h1><p style={s.logoSub}>YOUR BOURBON JOURNAL</p></div>
        <button onClick={handleSignOut} style={{ background: 'none', border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.textMuted, fontSize: 11, cursor: 'pointer', padding: '6px 10px', fontFamily: 'Georgia, serif', letterSpacing: 1 }}>Sign Out</button>
      </div>

      {screen === 'home' && <HomeScreen bottles={bottles} scoreMap={scoreMap} recentTastings={recentTastings} totalNotes={totalNotes} onNavigate={navigate} onExport={exportData} />}

      {screen === 'collection' && (
        <CollectionScreen bottles={bottles} scoreMap={scoreMap} onNavigate={navigate}
          initialFilter={collectionState.filter} initialTypeFilter={collectionState.typeFilter}
          initialSort={collectionState.sort} initialSortDir={collectionState.sortDir}
          initialMode={collectionState.mode} onStateChange={(state) => setCollectionState(state)} />
      )}

      {screen === 'detail' && (
        <DetailScreen bottle={selectedBottle} onNavigate={navigate} onDelete={loadData} onSave={loadData} onUpdateBottle={handleUpdateBottle} />
      )}

      {screen === 'tasting' && (
        <TastingScreen
          key={tastingKey}
          bottles={bottles}
          bottle={selectedBottle}
          tastingNote={null}
          onNavigate={navigate}
          onSave={loadData}
          onDiscard={() => setTastingKey(k => k + 1)}
          recentTastings={recentTastings}
          scoreMap={scoreMap}
        />
      )}

      {screen === 'edit-tasting' && (
        <TastingScreen
          bottles={bottles}
          bottle={selectedBottle}
          tastingNote={selectedNote}
          onNavigate={navigate}
          onSave={loadData}
          scoreMap={scoreMap}
        />
      )}

      {screen === 'add-bottle' && (
        <AddBottleScreen bottle={null} initialName={addBottleInitialName} onNavigate={navigate} onSave={loadData} />
      )}

      {screen === 'edit-bottle' && (
        <AddBottleScreen bottle={selectedBottle} onNavigate={navigate} onSave={loadData} />
      )}

      {screen === 'update-fill' && (
        <UpdateFillScreen bottle={selectedBottle} onNavigate={navigate} onSave={loadData} />
      )}

      {screen === 'wishlist' && <WishlistScreen />}

      {screen === 'search' && (
        <TastingSearchScreen bottles={bottles} onNavigate={navigate} />
      )}

      <nav style={s.nav}>
        {navItems.map(item => {
          const active = activeNav === item.id;
          return (
            <button key={item.id} style={s.navBtn(active)} onClick={() => navigate(item.id, null)}>
              <span style={s.navIcon}>{item.icon}</span>
              {item.label}
              {active && <div style={{ height: 2, width: 20, background: `linear-gradient(90deg, ${COLORS.accentDark}, ${COLORS.accent})`, borderRadius: 1, marginTop: 2 }} />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}